import { Grid, UI, Utils } from 'common';
import isArray from 'lodash/isArray';
import PropTypes from 'prop-types';
import React from 'react';
import DateCellRenderer from '../shared/DateCellRenderer.component';
import { RefundType } from '../shared/RefundTypeOptions';

const { GridPanel } = Grid;
const { WrappedButton, WrappedButtonType } = UI;
const { formatAddressToOneLine } = Utils.Util;

const usCurrencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
});

const columnDefs = [
	{
		field: 'transactionDate',
		headerName: 'Print Date',
		width: 110,
		cellRenderer: 'DateCellRenderer',
	},
	{
		field: 'recipientAddress',
		flex: 1,
		headerName: 'Recipient',
		width: 200,
		// valueGetter (not cellRenderer): ag-Grid inserts strings returned from a
		// function cellRenderer as HTML, and the address is user-controlled text.
		valueGetter({
			data: {
				printDetails: { toAddress, netstampSummary, refundType, printTypeName },
			},
		}) {
			return refundType === RefundType.MailIn && printTypeName === 'NetStamps'
				? netstampSummary
				: formatAddressToOneLine(toAddress, true);
		},
	},
	{
		field: 'refundType',
		headerName: 'Type',
		width: 120,
		valueGetter({ data: { printDetails } }) {
			return printDetails.refundTypeName;
		},
	},
	{
		field: 'totalAmount',
		headerName: 'Cost',
		type: 'numericColumn',
		width: 80,
		cellStyle: { textAlign: 'right' },
		hide: true,
		cellRenderer({ data: { totalAmount, printDetails } }) {
			const cost =
				printDetails.insuranceType === 'parcelguard' ||
				printDetails.carrier !== 'usps'
					? 0
					: totalAmount - printDetails.insuranceCost;
			return usCurrencyFormatter.format(cost);
		},
	},
	{
		field: 'insuredValue',
		flex: 1,
		headerName: 'Stamps.com Insurance',
		type: 'numericColumn',
		width: 150,
		cellStyle: { textAlign: 'right' },
		hide: true,
		cellRenderer({ data: { printDetails } }) {
			const cost =
				printDetails.insuranceType === 'parcelguard' ||
				printDetails.carrier !== 'usps'
					? 0
					: printDetails.insuranceCost;
			return usCurrencyFormatter.format(cost);
		},
	},
	{
		colId: 'RefundTotal',
		headerName: 'Refund Total',
		type: 'numericColumn',
		width: 130,
		cellStyle: { textAlign: 'right' },
		valueGetter: ({ data: { totalAmount } }) =>
			usCurrencyFormatter.format(totalAmount),
	},
];

const CertifiedRefundContent = ({ selectedPrintsForRefund = null }) => {
	const hasMailInRefundType = isArray(selectedPrintsForRefund)
		? selectedPrintsForRefund.some(
				({ printDetails }) => printDetails?.refundType === RefundType.MailIn
			)
		: false;

	const totalCost = isArray(selectedPrintsForRefund)
		? selectedPrintsForRefund.reduce(
				(total, { totalAmount }) => total + totalAmount,
				0
			)
		: 0;

	// check if any of the selected prints have PIP insurance
	selectedPrintsForRefund.forEach(element => {
		if (
			element.printDetails.insuranceCost > 0 &&
			element.printDetails.insuranceType !== 'parcelguard' &&
			element.printDetails.carrier === 'usps'
		) {
			columnDefs[3].hide = false;
			columnDefs[4].hide = false;
		} else {
			// hide cost and insured value columns if the amount is zero.
			columnDefs[3].hide = true;
			columnDefs[4].hide = true;
		}
	});

	return (
		<div className="history-refund certified-refund-content">
			<h3>Certify Refund Request</h3>
			{hasMailInRefundType && (
				<p>
					Important Notes: Claims for mail-in refunds must be sent within 60
					days of postage. NetStamps refunds are granted only for misprints that
					cannot be processed through the U.S. mail system. If the submitted
					NetStamps are found to be usable, they may be returned to you for use
					as valid U.S. postage. Misprinted postage must be unused. Items
					handled or returned to sender by the U.S. Postal Service are not
					eligible for refund (Insufficient Postage, Article Refused, etc.).
					Photocopies will not be accepted for refunds.
				</p>
			)}
			<GridPanel
				className="grid"
				columnDefs={columnDefs}
				defaultColDef={{
					suppressSizeToFit: true,
					suppressMovable: true,
					sortable: false,
					suppressMenu: true,
				}}
				domLayout="autoHeight"
				rowData={selectedPrintsForRefund}
				showPagination={false}
				frameworkComponents={{ DateCellRenderer }}
			/>
			<div className="total">
				<p>Total Request: {usCurrencyFormatter.format(totalCost)}</p>
			</div>
			<p>
				The filing of false, fictitious, or fraudulent refund requests may be
				punishable under the criminal laws of the United States. 18 U.S.C.
				§1001.
			</p>
			<p>
				By clicking Submit, you certify that the above labels submitted have
				been destroyed and were never used.
			</p>
		</div>
	);
};

CertifiedRefundContent.propTypes = {
	selectedPrintsForRefund: PropTypes.array,
};

export default CertifiedRefundContent;
